import { memo, useMemo, useState } from "react";
import { Box, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import BudgetGroupBlock, { GROUP_BODY_PREFIX } from "./index";
import GroupRow from "./GroupRow";
import NewGroupRow from "./NewGroupRow";
import NewRowForm from "./NewRowForm";
import ImportGroupsFromClient from "./ImportGroupsFromClient";
import {
  useBudgetActions,
  useBudgetMeta,
  useBudgetServicesState,
  useBudgetSuppliersState,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import {
  RenderableGroup,
  RenderableRow,
  UNGROUPED_ID,
  getRowsOfGroup,
  getVisibleGroups,
  rowKey,
} from "../../../../utils/budget-groups";
import {
  servicesOfRow,
  sumServiceTotals,
} from "../../../../utils/budget-group-totals";
import { CalcOption } from "../../../../types/budget/budget";
import { useProject } from "../../../../contexts/project.context";

/**
 * A que agrupamento aponta o alvo sob o cursor.
 *
 * O alvo pode ser o cabeçalho do agrupamento, o corpo dele (inclusive vazio) ou
 * outra linha — os três resolvem para o mesmo agrupamento.
 */
export function resolveOverGroupId(
  over: { id: string | number; data: { current?: any } },
  rowsById: Map<string, RenderableRow>,
  visibleGroups: RenderableGroup[]
): string | null {
  const overId = String(over.id);
  const kind = over.data?.current?.kind;

  if (kind === "group") return overId;
  if (kind === "group-body") return over.data?.current?.groupId ?? null;
  if (overId.startsWith(GROUP_BODY_PREFIX)) {
    return overId.slice(GROUP_BODY_PREFIX.length);
  }

  const row = rowsById.get(overId);
  if (row) return row.groupId ?? UNGROUPED_ID;

  return visibleGroups.some((group) => group.id === overId) ? overId : null;
}

/**
 * Visão "Por Agrupamento".
 *
 * A unidade dentro do agrupamento é a LINHA (categoria + fornecedor), e o que
 * separa os agrupamentos é o SERVIÇO — o mesmo par pode aparecer em vários
 * agrupamentos, cada um mostrando só os serviços dele.
 *
 * **Um único `DndContext` governa os dois níveis.** Aninhar contextos não
 * funcionaria: o de linhas precisa abranger TODOS os agrupamentos para permitir
 * arrastar entre eles, e o de agrupamentos precisa estar por fora. Com um
 * contexto só, cada arrasto se identifica por `active.data.current.kind`.
 */
const GroupedRows = memo(function GroupedRows() {
  const { activeCalcOption, versionInfo, isBudgetDisabled, activeBudgetId } =
    useBudgetMeta();
  const { addedItemTypeList, budgetGroups, itemTypeList } =
    useBudgetSuppliersState();
  const { budgetServices } = useBudgetServicesState();
  const { activeBudgetDispatch, runBudgetAutosave } = useBudgetActions();
  // A criação da linha nasce com o primeiro serviço — precisa das MESMAS
  // informações do `addOption` da visão plana (aba ativa e câmbio do evento).
  const { tabActive, project } = useProject();

  const calcOption = activeCalcOption as CalcOption;
  const version = versionInfo?.version ?? 0;
  const readOnly = !!isBudgetDisabled;

  /** Agrupamento cujo formulário "Novo fornecedor" está aberto. */
  const [formGroupId, setFormGroupId] = useState<string | null>(null);
  /**
   * Nome do agrupamento recém-criado, à espera do id.
   *
   * `CREATE_GROUP` gera o `id` dentro do reducer, então quem despacha não o
   * conhece. Guardamos o nome e abrimos o formulário no próximo render, quando
   * o agrupamento já existe na lista.
   */
  const [pendingFormForGroupName, setPendingFormForGroupName] = useState<
    string | null
  >(null);

  // Mesmo `activationConstraint` dos outros níveis: exigir 8px de movimento é o
  // que separa "clicar no seletor de fornecedor" de "arrastar".
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const visibleGroups = useMemo(
    () =>
      getVisibleGroups({
        budgetGroups,
        budgetServices,
        addedItemTypeList,
        calcOption,
        version,
        status: versionInfo?.status,
      }),
    [
      budgetGroups,
      budgetServices,
      addedItemTypeList,
      calcOption,
      version,
      versionInfo?.status,
    ]
  );

  const rowsByGroup = useMemo(() => {
    const map = new Map<string, RenderableRow[]>();
    visibleGroups.forEach((group) =>
      map.set(
        group.id,
        getRowsOfGroup({
          budgetGroups,
          budgetServices,
          addedItemTypeList,
          calcOption,
          version,
          status: versionInfo?.status,
          groupId: group.id,
        })
      )
    );
    return map;
  }, [
    visibleGroups,
    budgetGroups,
    budgetServices,
    addedItemTypeList,
    calcOption,
    version,
    versionInfo?.status,
  ]);

  const rowsById = useMemo(() => {
    const map = new Map<string, RenderableRow>();
    rowsByGroup.forEach((rows) =>
      rows.forEach((row) => map.set(rowKey(row), row))
    );
    return map;
  }, [rowsByGroup]);

  // Total do agrupamento = soma dos totais das linhas dele, pela MESMA fórmula
  // da árvore de totais, restrita aos serviços daquele agrupamento.
  const totalsByGroup = useMemo(() => {
    const map = new Map<string, number>();
    visibleGroups.forEach((group) => {
      const total = (rowsByGroup.get(group.id) ?? []).reduce((sum, row) => {
        const services = servicesOfRow(
          budgetServices,
          { ...row, calcOption },
          version,
          versionInfo?.status
        );
        return sum + sumServiceTotals(services, versionInfo).totals;
      }, 0);
      map.set(group.id, total);
    });
    return map;
  }, [
    visibleGroups,
    rowsByGroup,
    budgetServices,
    calcOption,
    version,
    versionInfo,
  ]);

  // O dispatch sozinho não faz a troca de aba dar flush (ver comentário em
  // BudgetItem/handleDragEnd).
  const flushAutosave = () => {
    if (!activeBudgetId) return;
    window.setTimeout(() => {
      void runBudgetAutosave("manual");
    }, 0);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    // Soltou fora de um alvo válido: nada muda.
    if (!over) return;

    const activeId = String(active.id);
    const targetGroupId = resolveOverGroupId(over, rowsById, visibleGroups);
    if (!targetGroupId) return;

    if (active.data.current?.kind === "group") {
      if (targetGroupId === activeId || targetGroupId === UNGROUPED_ID) return;
      const oldIndex = (budgetGroups ?? []).findIndex(
        (el) => el.id === activeId
      );
      const newIndex = (budgetGroups ?? []).findIndex(
        (el) => el.id === targetGroupId
      );
      if (oldIndex === -1 || newIndex === -1) return;

      activeBudgetDispatch({
        type: "REORDER_GROUPS",
        payload: { oldIndex, newIndex },
      });
      flushAutosave();
      return;
    }

    const row = rowsById.get(activeId);
    if (!row) return;
    const fromGroupId = row.groupId ?? UNGROUPED_ID;
    if (fromGroupId === targetGroupId) return;

    activeBudgetDispatch({
      type: "MOVE_ROW_TO_GROUP",
      payload: {
        type: row.type,
        supplierId: row.supplierId,
        calcOption,
        fromGroupId,
        targetGroupId,
      },
    });
    flushAutosave();
  };

  const handleDeleteGroup = (group: RenderableGroup) => {
    const rowCount = (rowsByGroup.get(group.id) ?? []).length;
    Swal.fire({
      title: `Excluir o agrupamento "${group.name}"?`,
      text: rowCount
        ? `As ${rowCount} linha(s) deste agrupamento NÃO serão excluídas — elas voltam para "Sem agrupamento", com os serviços.`
        : "O agrupamento está vazio e será removido.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Sim, excluir agrupamento",
    }).then((result) => {
      if (!result.isConfirmed) return;
      activeBudgetDispatch({
        type: "DELETE_GROUP",
        payload: { groupId: group.id },
        versionInfo,
      });
      flushAutosave();
    });
  };

  // Resolve o agrupamento recém-criado para o id, e abre o formulário nele.
  const createdGroup = pendingFormForGroupName
    ? [...(budgetGroups ?? [])]
        .reverse()
        .find(
          (el) =>
            el.calcOption === calcOption && el.name === pendingFormForGroupName
        )
    : undefined;
  if (createdGroup) {
    setPendingFormForGroupName(null);
    setFormGroupId(createdGroup.id);
  }

  const groupSortableIds = useMemo(
    () =>
      visibleGroups.filter((group) => group.isReal).map((group) => group.id),
    [visibleGroups]
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={groupSortableIds}
        strategy={verticalListSortingStrategy}
      >
        {visibleGroups.map((group) => {
          const rows = rowsByGroup.get(group.id) ?? [];
          const isFormOpen = formGroupId === group.id;

          return (
            <BudgetGroupBlock
              key={group.id}
              group={group}
              total={totalsByGroup.get(group.id) ?? 0}
              rowIds={rows.map(rowKey)}
              readOnly={readOnly}
              // Com o formulário aberto o botão tracejado dá lugar a ele, como
              // no protótipo — os dois são o mesmo controle em dois estados.
              hideAddButton={isFormOpen}
              onRename={(name) => {
                activeBudgetDispatch({
                  type: "RENAME_GROUP",
                  payload: { groupId: group.id, name },
                });
                flushAutosave();
              }}
              onDuplicate={() => {
                activeBudgetDispatch({
                  type: "DUPLICATE_GROUP",
                  payload: { groupId: group.id },
                });
                flushAutosave();
              }}
              onDelete={() => handleDeleteGroup(group)}
              onAddSupplier={() => setFormGroupId(group.id)}
              footer={
                isFormOpen ? (
                  <NewRowForm
                    groupName={group.name}
                    enabledCategories={itemTypeList?.[calcOption]}
                    onCancel={() => setFormGroupId(null)}
                    onCreate={(supplierId, type, supplierName) => {
                      activeBudgetDispatch({
                        type: "ADD_ROW_TO_GROUP",
                        payload: {
                          supplierId,
                          supplierName,
                          type,
                          calcOption,
                          groupId: group.isReal ? group.id : undefined,
                          tabActive,
                          projectEvent: project?.event,
                        },
                        versionInfo,
                      });
                      setFormGroupId(null);
                      flushAutosave();
                    }}
                  />
                ) : null
              }
            >
              {rows.map((row, rowIndex) => (
                <GroupRow
                  key={rowKey(row)}
                  sortableId={rowKey(row)}
                  row={row}
                  groupName={group.name}
                  calcOption={calcOption}
                  position={rowIndex + 1}
                  readOnly={readOnly}
                />
              ))}
            </BudgetGroupBlock>
          );
        })}
      </SortableContext>

      <ImportGroupsFromClient
        clientId={project?.client?._id}
        existingGroups={budgetGroups}
        disabled={readOnly}
        onImport={(groups) => {
          activeBudgetDispatch({
            type: "ADD_GROUPS_FROM_TEMPLATE",
            payload: { groups },
          });
          flushAutosave();
        }}
      />

      <Box sx={{ mt: 1 }}>
        <NewGroupRow
          disabled={readOnly}
          onCreate={(name) => {
            activeBudgetDispatch({
              type: "CREATE_GROUP",
              payload: { name, calcOption },
            });
            // O protótipo já abre o formulário dentro do agrupamento novo: ele
            // nasce vazio, e a próxima coisa a fazer é preencher.
            setPendingFormForGroupName(name.trim());
            flushAutosave();
          }}
        />
      </Box>
    </DndContext>
  );
});

export default GroupedRows;
